import { SelectDropdown, InputField } from "@sentara-health/optimahealth_web";
import { useFormContext } from "react-hook-form";
import { t } from "i18next";
import { FormError } from "../../organisms/form";
import ViewEligibilitySubmit from "./ViewEligibilitySubmit";
import {
    NameRegex,
    NumbersWithCharacters,
    AlphaNumeriAsterisks,
    NumericAsterisks,
    PolicyRegex,
    SSNRegex,
    MBIRegex,
    SubscriberIDRegex,
    AlphaNumeric
} from "utils/constants/Regex";
import {
    MBILength,
    SSNLength,
    VALIDATION_LENGTH,
    memberNumberLength,
} from "utils/constants/Constants";
import { Brands, useAuth, useBranding } from "providers";
import { NameChange, ssnOncheck, alphanumericSort } from "../../utils/helpers/HelperFn";
import { useEffect } from "react";
import { useNavigate } from "react-router";
import { VIEW_ELIGIBILITY } from "utils/constants/Routes";
import { USERLOBS, USERTYPE } from "utils/model/CommonTypes";
import { userTypeLob } from "utils/helpers/HelperFn";
import { ViewEligibility_AccountNumber } from "utils/constants/Routes";

interface ViewEligibilityFormProps {
    lob: string;
    lobChanged: boolean;
    clearForm: boolean;
    selectCriteriaValidation: Function;
    setHasDirtyFields: Function;
    setSearchError: Function;
    setAllFilteredGroupIds: Function;
  }

function ViewEligibilityForm({lob, lobChanged,clearForm, selectCriteriaValidation, setHasDirtyFields, setSearchError, setAllFilteredGroupIds} : ViewEligibilityFormProps ) {
    const { register, reset, resetField, formState, getValues, trigger, watch } =
        useFormContext();
    const { user, isAccess } = useAuth();
    const userType = userTypeLob(user);
    const navigate = useNavigate();
    const { brandConfig } = useBranding();

    // Watch for changes in accountId dropdown
    const selectedAccountId = watch("accountId");
    const uniqueAccounts = Array.from(
        new Map(
        user?.groups?.map((item) => [
            `${item.accountId}|${item.accountName}`,
            item,
        ])
        ).values()
      ).sort((a, b) => alphanumericSort(a, b, 'accountName'));

      // Filter groups based on selected account
      const filteredGroups = selectedAccountId
        ? user?.groups?.filter((group) => group?.accountId === selectedAccountId?.split('-')[0])?.sort((a, b) => {
          const aDisplay = `${a.groupID} ${a.groupName}`;
          const bDisplay = `${b.groupID} ${b.groupName}`;
          return alphanumericSort({ combined: aDisplay }, { combined: bDisplay }, 'combined');
        })
        : user?.groups?.sort((a, b) => {
          const aDisplay = `${a.groupID} ${a.groupName}`;
          const bDisplay = `${b.groupID} ${b.groupName}`;
          return alphanumericSort({ combined: aDisplay }, { combined: bDisplay }, 'combined');
        });

      // Handle account dropdown change
      const handleAccountChange = (e: any) => {
        // Reset groupId when account changes
        resetField("groupId");
        setSearchError({});
        selectCriteriaValidation(false)
      };

    useEffect(() => {
        if(lobChanged) {
            handleClearForm()
        }
    },[lob,lobChanged])

    useEffect(() => {
    if(clearForm) {
        handleClearForm()
    }
    },[clearForm])

    useEffect(() => {
        setAllFilteredGroupIds(filteredGroups?.map((group) => group.groupID) || []);
    }, [filteredGroups]);

    //For reseting the form
    const handleClearForm = () => {
        reset({
            groupId: "",
            policyId: "",
            memberId: "",
            subscriberSecurityNumber: "",
            memberBeneficiaryIdentifier:"",
            firstName: "",
            lastName: "",
            accountId: "",
        });
        setSearchError({})
        selectCriteriaValidation(false)
        if(!(USERTYPE.COMBINED === userType && lob === USERLOBS.MEDICARE && Object.keys(formState.dirtyFields).length > 0)) {
            navigate(`/app/${VIEW_ELIGIBILITY}`);
        }        
    };

    const searachByValdate = () => {
        const { accountId, groupId, searchInput, lineOfBusiness, ...rest } = getValues();
        const result: any = [];
        Object.keys(rest).forEach((key) => {
            if (key !== "firstName" && rest[key]) {
                result.push(rest[key]);
            }
        });
        if (!result.length) {
            selectCriteriaValidation(true)
            setSearchError({})
            return t("viewEligibilityForm.validation.eitherOneSelected");
        }
        selectCriteriaValidation(false)
    };
    const lastNameValidate = (value: string) => {
        const fistName = getValues("firstName");
        if (fistName && !value) {
            return t("viewEligibilityForm.validation.lastNameMust");
        }
    };

    register("searchInput", { validate: searachByValdate });
    setHasDirtyFields(formState.dirtyFields)

    return (
      <div>
        <div className="row">
          <div className="col-lg-8 col-md-8 col-sm-12 col-12">
          {isAccess(ViewEligibility_AccountNumber) &&
            lob?.toLowerCase() !== USERTYPE.MEDICARE.toLowerCase() && (
              <div className="mb-4">
                <label
                  className="pb-1"
                  htmlFor="selectAccount"
                  data-testid="lblAccountNumber"
                >
                  {t("orderMemberIDCard.selectAccount")}
                </label>
                <div>
                  <SelectDropdown
                    {...register("accountId", {
                      required: t("viewEligibilityForm.selectAccountOption"),
                      onChange: handleAccountChange,
                    })}
                    className={
                      formState.errors.accountId?.message
                        ? "form-select error-input-bdr"
                        : "form-select"
                    }
                    dataTestId="optOrderMemberIdCardAccountList"
                    id="order_id_account_id"
                    aria-describedby="order_id_account_number_error"
                  >
                    <option value="">
                      {t("orderMemberIDCard.allaccounts")}
                    </option>
                    {uniqueAccounts.map((account) => (
                      <option
                        key={`${account.accountId}-${account.accountName}`}
                        value={`${account.accountId}-${account.accountName}`}
                      >
                        {account.accountName}
                      </option>
                    ))}
                  </SelectDropdown>
                  <div
                    className={
                      formState.errors.accountId?.message ? "" : "d-none"
                    }
                  >
                    <div
                      id="order_id_account_number_error"
                      aria-live="polite"
                      aria-atomic="true"
                    >
                      <FormError name="accountId" />
                    </div>
                  </div>
                </div>
              </div>
            )}
            {lob?.toLowerCase() !== USERTYPE.MEDICARE.toLowerCase() && (
                <div className="mb-4">
                <label
                    className="pb-1"
                    htmlFor="order_id_group_id"
                    data-testid="lblOrderMemberIdCard"
                >
                    {t("viewEligibilityForm.selectGroupOptional")}
                </label>
                <div>
                    <SelectDropdown
                    {...register("groupId", {
                        required: false,
                        onChange: () => {
                        setSearchError({});
                        },
                    })}
                    className={
                        formState.errors.groupId?.message
                        ? "form-select error-input-bdr"
                        : "form-select"
                    }
                    dataTestId="optOrderMemberIdCardGroupList"
                    disabled={!selectedAccountId}
                    id="order_id_group_id"
                    aria-describedby="order_id_group_id_error"
                    >
                    <option value="">{t("viewEligibilityForm.allgroups")}</option>
                    {selectedAccountId &&
                        filteredGroups?.map((item) => {
                        return (
                            <option key={`${item.groupID}`} value={item.groupID}>
                            {item.groupID} {item.groupName}
                            </option>
                        );
                        })}
                    </SelectDropdown>
                    <div
                    className={formState.errors.groupId?.message ? "" : "d-none"}
                    >
                    <div
                        id="order_id_group_id_error"
                        aria-live="polite"
                        aria-atomic="true"
                    >
                        <FormError name="groupId" />
                    </div>
                    </div>
                </div>
                </div>
            )}

            {(userType !== USERTYPE.MEDICARE || lob !== USERLOBS.MEDICARE) && lob !== USERLOBS.MEDICARE && <div className="pos-rel pt-2">
              <hr className="m-0" />
              <strong className="or-separator">
                {t("viewEligibilityForm.and")}
              </strong>
            </div>}

            {lob !== USERLOBS.MEDICARE && (
              <>
                <div className="mb-4">
                  <label
                    className="pb-1"
                    htmlFor="viewEligibilityPolicyId"
                    data-testid="lblPolicyId"
                  >
                    {t("viewEligibilityForm.policyIdLabel")}
                  </label>
                  <div>
                    <InputField
                      pattern={brandConfig.brandName === Brands.AVMED ? AlphaNumeric : NumbersWithCharacters}
                      maxLength={VALIDATION_LENGTH.POLICY_ID_MAX_LENGTH}
                      {...register("policyId", {
                        // pattern: {
                        //   value: brandConfig.brandName === Brands.AVMED ? AlphaNumeric : PolicyRegex,
                        //   message: t("viewEligibilityForm.validationPolicyID"),
                        // },
                        onChange: () => {
                          trigger("searchInput");
                          setSearchError({});
                          resetField("firstName");
                          resetField("lastName");
                          resetField("memberId");
                          resetField("subscriberSecurityNumber");
                          resetField("memberBeneficiaryIdentifier");
                        },
                      })}
                      className={
                        formState.errors.policyId?.message
                          ? "form-input error-input-bdr"
                          : "form-input"
                      }
                      placeholder={t("viewEligibilityForm.plcHolderPolicyId")}
                      dataTestId="inputPolicyId"
                      id="viewEligibilityPolicyId"
                      aria-describedby="policy_id__error"
                    />
                    <div
                      className={
                        formState.errors.policyId?.message ? "" : "d-none"
                      }
                    >
                      <div
                        id="policy_id__error"
                        aria-live="polite"
                        aria-atomic="true"
                      >
                        <FormError name="policyId" />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="pos-rel pt-2">
                  <hr className="m-0" />
                  <strong className="or-separator">
                    {t("viewEligibilityForm.or")}
                  </strong>
                </div>
              </>
            )}

            <div className="mb-4">
              <label
                className="pb-1"
                htmlFor="member_id"
                data-testid="lblMemberId"
              >
                {t("viewEligibilityForm.MemberIdLabel")}
              </label>
              <div>
                <InputField
                  maxLength={VALIDATION_LENGTH.MEMBER_ID_MAX_LENGTH}
                  pattern={brandConfig.brandName === Brands.AVMED ? AlphaNumeriAsterisks : NumericAsterisks}
                  {...register("memberId", {
                    minLength: {
                      value: memberNumberLength,
                      message: t("viewEligibilityForm.memberNumberLength"),
                    },
                    // pattern: {
                    //   value: SubscriberIDRegex,
                    //   message: t("viewEligibilityForm.validateMemberID"),
                    // },
                    onChange: () => {
                      trigger("searchInput");
                      setSearchError({});
                      resetField("firstName");
                      resetField("lastName");
                      resetField("policyId");
                      resetField("subscriberSecurityNumber");
                      resetField("memberBeneficiaryIdentifier");
                    },
                  })}
                  className={
                    formState.errors.memberId?.message
                      ? "form-input error-input-bdr"
                      : "form-input"
                  }
                  placeholder={t("viewEligibilityForm.plcHolderMemberId")}
                  dataTestId="inputMemberId"
                  id="member_id"
                  aria-describedby="member_id_error"
                />
                <div
                  className={formState.errors.memberId?.message ? "" : "d-none"}
                >
                  <div
                    id="member_id_error"
                    aria-live="polite"
                    aria-atomic="true"
                  >
                    <FormError name="memberId" />
                  </div>
                </div>
                <p className="granite-gray-color pt-1">
                  <small>{t("viewEligibilityForm.includeAsterisk")}</small>
                </p>
              </div>
            </div>
            <div className="pos-rel pt-2">
              <hr className="m-0" />
              <strong className="or-separator">
                {t("viewEligibilityForm.or")}
              </strong>
            </div>
            {lob !== USERLOBS.MEDICARE && (
              <>
                <div className="mb-4">
                  <label
                    className="pb-1"
                    htmlFor="ss_number"
                    data-testid="lblSubscriberSSN"
                  >
                    {t("viewEligibilityForm.sssnLabel")}
                  </label>
                  <div>
                    <InputField
                      maxLength={VALIDATION_LENGTH.SSN_MIN_LENGTH}
                      {...register("subscriberSecurityNumber", {
                        minLength: {
                          value: SSNLength,
                          message: t("viewEligibilityForm.SSNLength"),
                        },
                        setValueAs: (v) => ssnOncheck(v),
                        onChange: (e) => {
                          e.target.value = ssnOncheck(e.target.value);
                          trigger("searchInput");
                          setSearchError({});
                          resetField("firstName");
                          resetField("lastName");
                          resetField("policyId");
                          resetField("memberId");
                          resetField("memberBeneficiaryIdentifier");
                        },
                        pattern: {
                          value: SSNRegex,
                          message: t("viewEligibilityForm.validationSSN"),
                        },
                      })}
                      forceNumber={true}
                      className={
                        formState.errors.subscriberSecurityNumber?.message
                          ? "form-input error-input-bdr"
                          : "form-input"
                      }
                      placeholder={t("viewEligibilityForm.plcSSSNumber")}
                      dataTestId="inputSSN"
                      id="ss_number"
                      aria-describedby="ss_number_error"
                    />
                    <div
                      className={
                        formState.errors.subscriberSecurityNumber?.message
                          ? ""
                          : "d-none"
                      }
                    >
                      <div
                        id="ss_number_error"
                        aria-live="polite"
                        aria-atomic="true"
                      >
                        <FormError name="subscriberSecurityNumber" />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="pos-rel pt-2">
                  <hr className="m-0" />
                  <strong className="or-separator">
                    {t("viewEligibilityForm.or")}
                  </strong>
                </div>
              </>
            )}

            {lob === USERLOBS.MEDICARE && (
              <>
                <div className="mb-4">
                  <label
                    className="pb-1"
                    htmlFor="mbi_number"
                    data-testid="lblSubscriberMBI"
                  >
                    {t("viewEligibilityForm.mbiLabel")}
                  </label>
                  <div>
                    <InputField
                      maxLength={VALIDATION_LENGTH.MBI_MAX_LENGTH}
                      {...register("memberBeneficiaryIdentifier", {
                        minLength: {
                          value: MBILength,
                          message: t("viewEligibilityForm.MBINumberLength"),
                        },

                        onChange: (e) => {
                          trigger("searchInput");
                          setSearchError({});
                          resetField("firstName");
                          resetField("lastName");
                          resetField("memberId");
                        },
                        pattern: {
                          value: MBIRegex,
                          message: t("viewEligibilityForm.validationMBI"),
                        },
                      })}
                      className={
                        formState.errors.memberBeneficiaryIdentifier?.message
                          ? "form-input error-input-bdr"
                          : "form-input"
                      }
                      placeholder={t("viewEligibilityForm.plcMBINumber")}
                      dataTestId="inputMBI"
                      id="mbi_number"
                      aria-describedby="mbi_number_error"
                    />
                    <div
                      className={
                        formState.errors.memberBeneficiaryIdentifier?.message
                          ? ""
                          : "d-none"
                      }
                    >
                      <div
                        id="mbi_number_error"
                        aria-live="polite"
                        aria-atomic="true"
                      >
                        <FormError name="memberBeneficiaryIdentifier" />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="pos-rel pt-2">
                  <hr className="m-0" />
                  <strong className="or-separator">
                    {t("viewEligibilityForm.or")}
                  </strong>
                </div>
              </>
            )}

            <div className="mb-4">
              <label
                className="pb-1"
                htmlFor="member_last_name"
                data-testid="lblMemberLastName"
              >
                {t("viewEligibilityForm.lastNameLabel")}
              </label>
              <div>
                <InputField
                  pattern={NameRegex}
                  maxLength={VALIDATION_LENGTH.NAME_MAX_LENGTH}
                  type="text"
                  forceChar={true}
                  {...register("lastName", {
                    validate: lastNameValidate,
                    onChange: (e) => {
                      NameChange(e);
                      trigger("searchInput");
                      setSearchError({});
                      lob === USERLOBS.MEDICARE
                        ? reset({
                            memberId: "",
                            subscriberSecurityNumber: "",
                            memberBeneficiaryIdentifier: "",
                            policyId: "",
                            groupId: "",
                          })
                        : 
                        resetField("memberId");
                        resetField("subscriberSecurityNumber");
                        resetField("memberBeneficiaryIdentifier");
                        resetField("policyId");
                    },
                  })}
                  className={
                    formState.errors.lastName?.message
                      ? "form-input error-input-bdr"
                      : "form-input"
                  }
                  placeholder={t("viewEligibilityForm.plcHolderLastName")}
                  dataTestId="inputMemberLastName"
                  id="member_last_name"
                  aria-describedby="member_last_name_error"
                />
                <div
                  className={formState.errors.lastName?.message ? "" : "d-none"}
                >
                  <div
                    id="member_last_name_error"
                    aria-live="polite"
                    aria-atomic="true"
                  >
                    <FormError name="lastName" />
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-4">
              <label
                className="pb-1"
                htmlFor="member_first_name"
                data-testid="lblMemberFirstName"
              >
                {t("viewEligibilityForm.firstNameLabel")}
              </label>
              <div>
                <InputField
                  pattern={NameRegex}
                  maxLength={VALIDATION_LENGTH.NAME_MAX_LENGTH}
                  {...register("firstName", {
                    onChange: (e) => {
                      NameChange(e);
                      trigger("lastName");
                      setSearchError({});
                      resetField("policyId");
                      resetField("memberId");
                      resetField("subscriberSecurityNumber");
                      resetField("memberBeneficiaryIdentifier");
                    },
                  })}
                  className={
                    formState.errors.firstName?.message
                      ? "form-input error-input-bdr"
                      : "form-input"
                  }
                  placeholder={t("viewEligibilityForm.plcHolderFirstName")}
                  dataTestId="inputMemberFirstName"
                  id="member_first_name"
                  aria-describedby="member_first_name_error"
                />
                <div
                  className={
                    formState.errors.firstName?.message ? "" : "d-none"
                  }
                >
                  <div
                    id="member_first_name_error"
                    aria-live="polite"
                    aria-atomic="true"
                  >
                    <FormError name="firstName" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <hr className="mt-0 mb-4" />
        <ViewEligibilitySubmit
          isFormDirty={
              ((lob === USERLOBS.MEDICARE || getValues("accountId")) &&
              (getValues("lastName") ||
                getValues("policyId") ||
                getValues("subscriberSecurityNumber") ||
                getValues("memberId") ||
                getValues("memberBeneficiaryIdentifier"))) ||
            (lob === USERLOBS.MEDICARE &&
              (getValues("memberId") ||
                getValues("memberBeneficiaryIdentifier") ||
                getValues("lastName") ||
                (getValues("firstName") && getValues("lastName"))))
          }
          handleClearForm={handleClearForm}
        />
      </div>
    );
}

export default ViewEligibilityForm;
